import { useEffect, useState } from "react";
import { Progress, Button, Typography, Space, Tag, List, message } from "antd";
import axios from "axios";
import useLoteNotificacionAPI, {
    EstadoLoteNotificacion as Estado,
    EstadoGrupoNotificacion,
    TandaEnGrupo,
    TIPO_LOTE_DISPLAY,
    tipoLoteLabel,
} from "../hooks/useLoteNotificacionAPI";

const { Text } = Typography;

export interface ResumenEstado {
    cantidadActas: number
    cantidadExitosas: number
    cantidadFallidas: number
    cantidadDescartadas: number
    cantidadPendientes: number
    completado: boolean
    activo: boolean
}

export interface EstadoLoteNotificacionProps {
    // Alcanza con el id de entrada (el primer lote de la tanda, o cualquier lote del historial):
    // el componente descubre solo si pertenece a un grupo de campaña partido en tandas y a partir
    // de ahí pollea el estado agregado del grupo en vez del de un solo lote.
    loteNotificacionId: number
    onEstadoChange?: (resumen: ResumenEstado) => void
    onVerDetalle?: (loteNotificacionId: number) => void
    // Para mostrar como indicador ambiental (p. ej. en la pestaña "Generar envío" mientras el
    // usuario sigue armando otro filtro): solo la barra de progreso + a qué lote corresponde,
    // sin tag de tipo, sin el desglose exitosas/fallidas/pendientes, sin tandas ni botón de
    // reanudar — todo eso ya está a un click via "Ver detalle" (el modal sí muestra todo).
    compact?: boolean
}

type ModoEstado =
    | { modo: "single"; estado: Estado }
    | { modo: "grupo"; estado: EstadoGrupoNotificacion };

const primerLoteReanudable = (lotes: TandaEnGrupo[]): TandaEnGrupo | undefined =>
    lotes.find((l) => !l.completado);

export default (props: EstadoLoteNotificacionProps) => {
    const api = useLoteNotificacionAPI();
    const [modoEstado, setModoEstado] = useState<ModoEstado | null>(null);
    const [reanudandoId, setReanudandoId] = useState<number | null>(null);

    useEffect(() => {
        let stopped = false;
        let intervalId: ReturnType<typeof setInterval>;
        let grupoId: number | null = null;

        const reportar = (r: ResumenEstado) => props.onEstadoChange?.(r);

        const tick = async () => {
            if (grupoId == null) {
                const result = await api.obtenerEstadoLote(props.loteNotificacionId);
                if (stopped || !result.data) return;
                const estado = result.data.value;
                if (estado.loteNotificacionGrupoId != null) {
                    grupoId = estado.loteNotificacionGrupoId;
                } else {
                    setModoEstado({ modo: "single", estado });
                    reportar(estado);
                    if (estado.completado) clearInterval(intervalId);
                    return;
                }
            }

            const grupoResult = await api.obtenerEstadoGrupo(grupoId);
            if (stopped || !grupoResult.data) return;
            const estado = grupoResult.data.value;
            setModoEstado({ modo: "grupo", estado });
            reportar(estado);
            if (estado.completado) clearInterval(intervalId);
        };

        tick();
        intervalId = setInterval(tick, 4000);
        return () => {
            stopped = true;
            clearInterval(intervalId);
        };
    }, [props.loteNotificacionId]);

    const reanudar = async (id: number) => {
        setReanudandoId(id);
        const result = await api.reanudarLote(id);
        setReanudandoId(null);
        if (result.error) {
            const es409 = axios.isAxiosError(result.error) && result.error.response?.status === 409;
            message.error(es409 ? "Ese lote ya se está procesando, esperá a que termine" : "Error al reanudar el lote");
            return;
        }
        message.success(`Reanudando ${result.data!.value.cantidadPendientes} actas pendientes...`);
    };

    if (!modoEstado) {
        return <Text>Cargando estado del lote...</Text>;
    }

    const progreso = (resumen: ResumenEstado) => {
        const percent = resumen.cantidadActas > 0
            ? Math.round(((resumen.cantidadActas - resumen.cantidadPendientes) / resumen.cantidadActas) * 100)
            : 100;
        return (
            <Space direction="vertical" style={{ width: "100%" }}>
                <Progress
                    percent={percent}
                    status={!resumen.completado ? "active" : (resumen.cantidadFallidas > 0 ? "exception" : "success")}
                />
                <Text>
                    {resumen.cantidadExitosas} enviadas, {resumen.cantidadFallidas} fallidas,{" "}
                    {resumen.cantidadDescartadas > 0 && <>{resumen.cantidadDescartadas} descartadas (sin email, convenio, etc.),{" "}</>}
                    {resumen.cantidadPendientes} pendientes de {resumen.cantidadActas} actas
                </Text>
            </Space>
        );
    };

    if (props.compact) {
        const idLabel = modoEstado.modo === "single"
            ? `Lote de envío #${modoEstado.estado.loteNotificacionId}`
            : `Grupo de envío #${modoEstado.estado.loteNotificacionGrupoId} (${modoEstado.estado.lotes.length} tandas)`;
        const verDetalleId = modoEstado.modo === "single"
            ? modoEstado.estado.loteNotificacionId
            : modoEstado.estado.lotes.find((l) => l.activo)?.loteNotificacionId ?? modoEstado.estado.lotes[0]?.loteNotificacionId;
        return (
            <Space direction="vertical" style={{ width: "100%" }}>
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                    <Text strong>{idLabel}</Text>
                    {props.onVerDetalle && verDetalleId != null && (
                        <Button size="small" type="link" onClick={() => props.onVerDetalle!(verDetalleId)}>Ver detalle</Button>
                    )}
                </Space>
                {progreso(modoEstado.estado)}
            </Space>
        );
    }

    if (modoEstado.modo === "single") {
        const { estado } = modoEstado;
        return (
            <Space direction="vertical" style={{ width: "100%" }}>
                <Space>
                    <Text strong>Lote de envío #{estado.loteNotificacionId}</Text>
                    <Tag color={tipoLoteLabel(estado.tipo) === "PRODUCCION" ? "blue" : TIPO_LOTE_DISPLAY[tipoLoteLabel(estado.tipo)].color}>
                        {TIPO_LOTE_DISPLAY[tipoLoteLabel(estado.tipo)].texto}
                    </Tag>
                </Space>
                {estado.loteImpresionNroLote != null && (
                    <Text type="secondary">Generado desde el lote de impresión #{estado.loteImpresionNroLote}</Text>
                )}
                {progreso(estado)}
                <Space>
                    {props.onVerDetalle && (
                        <Button onClick={() => props.onVerDetalle!(estado.loteNotificacionId)}>Ver detalle</Button>
                    )}
                    {!estado.completado && !estado.activo && (
                        <Button onClick={() => reanudar(estado.loteNotificacionId)} loading={reanudandoId === estado.loteNotificacionId} danger>
                            Reanudar (se cortó a mitad de camino)
                        </Button>
                    )}
                </Space>
            </Space>
        );
    }

    // Modo grupo: campaña (o producción vencida) partida en varias tandas de hasta 1000 actas, procesadas de a una.
    const { estado } = modoEstado;
    const tandaCortada = primerLoteReanudable(estado.lotes);
    const mostrarReanudar = tandaCortada && !tandaCortada.activo;
    const tipoGrupo = TIPO_LOTE_DISPLAY[tipoLoteLabel(estado.tipo)];

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <Space>
                <Text strong>Grupo de envío #{estado.loteNotificacionGrupoId}</Text>
                <Tag color={tipoGrupo.color}>{tipoGrupo.texto} — {estado.lotes.length} tandas</Tag>
            </Space>
            {estado.loteImpresionId != null && (
                <Text type="secondary">Generado desde el lote de impresión #{estado.loteImpresionId}</Text>
            )}
            {progreso(estado)}
            <List
                size="small"
                bordered
                dataSource={estado.lotes}
                renderItem={(tanda) => (
                    <List.Item
                        actions={[
                            props.onVerDetalle && (
                                <Button size="small" type="link" onClick={() => props.onVerDetalle!(tanda.loteNotificacionId)}>Ver detalle</Button>
                            ),
                            mostrarReanudar && tandaCortada?.loteNotificacionId === tanda.loteNotificacionId && (
                                <Button size="small" danger onClick={() => reanudar(tanda.loteNotificacionId)} loading={reanudandoId === tanda.loteNotificacionId}>
                                    Reanudar
                                </Button>
                            ),
                        ].filter(Boolean)}
                    >
                        <Space>
                            <Text strong>Tanda {tanda.ordenEnGrupo} (lote #{tanda.loteNotificacionId})</Text>
                            <Text type="secondary">
                                {tanda.cantidadExitosas} / {tanda.cantidadFallidas} / {tanda.cantidadPendientes} de {tanda.cantidadActas}
                                {tanda.cantidadDescartadas > 0 && ` (${tanda.cantidadDescartadas} descartadas)`}
                            </Text>
                            {tanda.completado && <Tag color={tanda.cantidadFallidas > 0 ? "orange" : "green"}>Completada</Tag>}
                            {!tanda.completado && tanda.activo && <Tag color="blue">En curso</Tag>}
                            {!tanda.completado && !tanda.activo && <Tag color="red">Cortada</Tag>}
                        </Space>
                    </List.Item>
                )}
            />
        </Space>
    );
};
